export interface RuntimeConfig {
  apiBaseUrl: string;
  socketUrl: string;
  buildVersion: string;
  telegramSecretToken?: string;
}

const PRODUCTION_API_BASE = 'https://api.ailexyapp.com';
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);

// Determine API base URL: use env var, or detect from current location, or fallback
// NOTE: This function is called at runtime, not at build time, to ensure window is available
const getApiBaseUrl = (): string => {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname?.toLowerCase() ?? '';
    const port = window.location.port;
    if (LOCAL_HOSTS.has(host)) {
      const baseHost = host.includes('::') ? '[::1]' : host;
      if (!port || port.length === 0) {
        return `http://${baseHost}:3001`;
      }
      if (port === '3000' || port === '5173' || port === '4173') {
        return `http://${baseHost}:3001`;
      }
      return `http://${baseHost}:3001`;
    }
  }

  return PRODUCTION_API_BASE;
};

// Default config (will be overridden by runtime detection)
const envConfig: RuntimeConfig = {
  apiBaseUrl: PRODUCTION_API_BASE,
  socketUrl: import.meta.env.VITE_SOCKET_URL ?? PRODUCTION_API_BASE,
  buildVersion: import.meta.env.VITE_BUILD_VERSION ?? import.meta.env.BUILD_VERSION ?? 'dev',
  telegramSecretToken: import.meta.env.VITE_TG_SECRET_TOKEN,
};

async function fetchRemoteConfig(): Promise<Partial<RuntimeConfig>> {
  try {
    const response = await fetch('/config.json', {
      cache: 'no-store',
    });

    if (!response.ok) {
      console.warn('[config] runtime config fetch failed:', response.status);
      return {};
    }

    const data = (await response.json()) as Partial<RuntimeConfig>;
    return data;
  } catch (error) {
    console.warn('[config] runtime config not available:', error);
    return {};
  }
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // Determine API URL at runtime (not at build time)
  const detectedApiUrl = getApiBaseUrl();
  
  const remote = await fetchRemoteConfig();
  return {
    apiBaseUrl: remote.apiBaseUrl ?? detectedApiUrl,
    socketUrl: remote.socketUrl ?? detectedApiUrl,
    buildVersion: remote.buildVersion ?? envConfig.buildVersion,
    telegramSecretToken: envConfig.telegramSecretToken,
  };
}
